import { z } from "zod";
import { bookTourPayload, leadFormPayload } from "./actions/contract";
import type { ChatStreamEvent } from "./chat-events";
import type { ChatAction, ChatEvent } from "./types";
import { workflowRunRef, workflowStepPayload } from "./workflow/contract";

const STAGES = ["understanding", "retrieving", "reranking", "generating", "validating"] as const;

/** Optional field the server may send as `null` (Pydantic `None`): null → undefined. */
function nullable<T extends z.ZodTypeAny>(schema: T) {
  return schema.nullish().transform((value): z.output<T> | undefined => value ?? undefined);
}

const meta = z.object({
  conversation_id: z.string(),
  message_id: z.string(),
  new_conversation: z.boolean().optional(),
});
const status = z.object({ stage: z.enum(STAGES) });
const token = z.union([z.object({ text: z.string() }), z.string().transform((text) => ({ text }))]);
const citation = z.object({
  n: z.number().int().positive(),
  source_id: z.string(),
  chunk_id: z.string(),
  title: z.string(),
  // A text snippet or a pasted Q&A has no page or section: the server sends null, the source is still cited.
  location: z.string().nullish().transform((value) => value ?? ""),
  snippet: z.string(),
  url: nullable(z.string()),
  score: nullable(z.number()),
  source_type: z
    .enum(["pdf", "docx", "xlsx", "csv", "pptx", "url", "live", "action", "handbook", "calendar", "fees", "memo", "text", "image"])
    .optional()
    .catch(undefined),
  updated_at: nullable(z.string()),
});
/** One `{type, payload}` action: a live `action` event, a stored transcript entry or a started run's message. */
export const chatActionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("book_tour"), payload: bookTourPayload }),
  z.object({ type: z.literal("lead_form"), payload: leadFormPayload }),
  z.object({ type: z.literal("workflow_step"), payload: workflowStepPayload }),
  z.object({ type: z.literal("handoff"), payload: z.object({ team: z.string(), eta_text: z.string().optional() }) }),
  z.object({ type: z.literal("handoff_offer"), payload: z.object({ team: z.string() }) }),
  z.object({ type: z.literal("custom"), payload: z.record(z.unknown()) }),
  /** The assistant's ordering cards (SD-S2-17); the lazy `orders` chunk decodes the payload (`@sagot/chat/orders`), the panel only carries it. */
  z.object({ type: z.literal("commerce_card"), payload: z.record(z.unknown()) }),
]);
const revise = z.object({ text: z.string(), reason: z.string() });
const done = z.object({
  usage: z.object({ in: z.number(), out: z.number() }).catch({ in: 0, out: 0 }),
  latency_ms: z.number().catch(0),
  confidence: z.number().catch(0),
  grounded: nullable(z.boolean()),
  gap: nullable(z.boolean()),
  credits: z.number().catch(0),
  followups: z.array(z.string()).catch([]),
  ai_paused: nullable(z.boolean()).catch(undefined),
  awaiting_staff: nullable(z.boolean()).catch(undefined),
  team: nullable(z.string()).catch(undefined),
  workflow: nullable(workflowRunRef).catch(undefined),
  workflow_message: nullable(z.literal(true)).catch(undefined),
});
const error = z.object({ code: z.string(), message: z.string(), retry_after: z.number().optional() });

/** Decodes one action (`{type, payload}`) of a stream event or of a stored transcript message; malformed → null. */
export function decodeActionEvent(raw: unknown): ChatAction | null {
  const parsed = chatActionSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/** Validates a raw contract event; malformed payloads are dropped (never trusted). */
export function decodeChatEvent(event: ChatStreamEvent): ChatEvent | null {
  const id = event.id;
  switch (event.type) {
    case "meta": {
      const r = meta.safeParse(event.data);
      return r.success ? { type: "meta", id, data: r.data } : null;
    }
    case "status": {
      const r = status.safeParse(event.data);
      return r.success ? { type: "status", id, data: r.data } : null;
    }
    case "token": {
      const r = token.safeParse(event.data);
      return r.success ? { type: "token", id, data: r.data } : null;
    }
    case "citations": {
      const r = z.array(citation).safeParse(event.data);
      return r.success ? { type: "citations", id, data: r.data } : null;
    }
    case "action": {
      const r = chatActionSchema.safeParse(event.data);
      return r.success ? { type: "action", id, data: r.data } : null;
    }
    case "revise": {
      const r = revise.safeParse(event.data);
      return r.success ? { type: "revise", id, data: r.data } : null;
    }
    case "done": {
      const r = done.safeParse(event.data);
      return r.success ? { type: "done", id, data: r.data } : null;
    }
    case "error": {
      const r = error.safeParse(event.data);
      return { type: "error", id, data: r.success ? r.data : { code: "unknown", message: "" } };
    }
  }
}
